import React, { useEffect, useState, useCallback } from "react";
import "./Analytics.css";

function fmtTime(iso) {
  try {
    const d = new Date(iso && iso.endsWith("Z") ? iso : iso + "Z");
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtDur(ms) {
  if (!ms || ms < 0) return "0s";
  const s = Math.round(ms / 1000);
  if (s < 60) return s + "s";
  const m = Math.floor(s / 60), r = s % 60;
  return m + "m" + (r ? " " + r + "s" : "");
}

function Bars({ items, color }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  if (!items.length) return <div className="an-empty">No data yet.</div>;
  return (
    <div className="an-bars">
      {items.map((it, i) => (
        <div className="an-bar-row" key={i}>
          <span className="an-bar-label" title={it.name}>{it.name}</span>
          <span className="an-bar-track">
            <span
              className="an-bar-fill"
              style={{ width: `${(it.count / max) * 100}%`, background: color || "var(--accent)" }}
            />
          </span>
          <span className="an-bar-count">{it.count}</span>
        </div>
      ))}
    </div>
  );
}

// Plain-language one-liner describing what a visitor did on the landing page.
function engSummaryLine(e) {
  const touched = (e.fields || []).join(" → ") || "none";
  const bits = [`stayed ${fmtDur(e.dwell_ms)}`, `scrolled ${e.max_scroll}%`];
  if (!e.saw_form) bits.push("never reached the form");
  else if (!e.form_focus) bits.push("saw the form but didn't tap it");
  else if (e.submitted) bits.push("submitted the form");
  else bits.push(`focused the form (${touched}) then left${e.last_field ? ` at "${e.last_field}"` : ""}`);
  if (e.calls) bits.push(`${e.calls} call tap${e.calls > 1 ? "s" : ""}`);
  if (e.quotes) bits.push(`${e.quotes} quote tap${e.quotes > 1 ? "s" : ""}`);
  return bits.join(" · ");
}

function EngagementDetail({ e }) {
  if (!e) {
    return (
      <div className="an-eng-empty">
        No behaviour data for this visit &mdash; either it wasn't a landing-page (/lp/) visit,
        or the visitor left before it was recorded.
      </div>
    );
  }
  const touched = (e.fields || []).join(" → ") || "none";
  const items = [
    ["Time on page", fmtDur(e.dwell_ms)],
    ["Active time", fmtDur(e.active_ms)],
    ["Scroll depth", e.max_scroll + "%"],
    ["Saw form", e.saw_form ? "Yes" : "No"],
    ["Focused form", e.form_focus ? "Yes" : "No"],
    ["Started typing", e.form_started ? "Yes" : "No"],
    ["Submitted", e.submitted ? "Yes" : "No"],
    ["Fields touched", touched],
    ["Abandoned at", (e.form_focus && !e.submitted && e.last_field) ? e.last_field : "—"],
    ["Call taps", String(e.calls || 0)],
    ["Quote taps", String(e.quotes || 0)],
  ];
  return (
    <div className="an-eng">
      <div className="an-eng-summary">{engSummaryLine(e)}</div>
      <div className="an-eng-grid">
        {items.map((it, i) => (
          <div className="an-eng-item" key={i}>
            <div className="an-eng-k">{it[0]}</div>
            <div className="an-eng-v">{it[1]}</div>
          </div>
        ))}
      </div>
      <div className="an-eng-scrollbar" title={`Scrolled ${e.max_scroll}% of the page`}>
        <div className="an-eng-scrollbar-fill" style={{ width: `${e.max_scroll}%` }} />
      </div>
    </div>
  );
}

function Analytics({ onAuthError }) {
  const [data, setData] = useState(null);
  const [eng, setEng] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) { if (onAuthError) onAuthError(); return; }
    setLoading(true);
    setErr(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [vRes, eRes] = await Promise.all([
        fetch("/api/visits", { headers }),
        fetch("/api/engagement", { headers }),
      ]);
      if (vRes.status === 401 || eRes.status === 401) { if (onAuthError) onAuthError(); return; }
      if (!vRes.ok) throw new Error("HTTP " + vRes.status);
      setData(await vRes.json());
      if (eRes.ok) setEng(await eRes.json());
    } catch (e) {
      setErr("Could not load analytics.");
    } finally {
      setLoading(false);
    }
  }, [onAuthError]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  if (loading && !data) {
    return <div className="analytics"><div className="an-note">Loading analytics…</div></div>;
  }
  if (err && !data) {
    return (
      <div className="analytics">
        <div className="an-note">{err} <button className="an-refresh" onClick={load}>Retry</button></div>
      </div>
    );
  }

  const s = data.summary;
  const t = s.totals;
  const recent = data.recent || [];
  const dailyMax = Math.max(1, ...s.daily.map((d) => d.count));
  const devTotal = Math.max(1, s.devices.mobile + s.devices.desktop + s.devices.tablet);
  const es = eng && eng.summary;

  const tiles = [
    { label: "Last 24 hours", big: t.visits_24h, uniq: t.uniques_24h },
    { label: "Last 7 days", big: t.visits_7d, uniq: t.uniques_7d },
    { label: "Last 30 days", big: t.visits_30d, uniq: t.uniques_30d },
  ];

  return (
    <div className="analytics">
      <div className="an-head">
        <div>
          <h2>Who's visiting</h2>
          <span className="an-sub">
            First-party analytics, self-hosted. Page views and unique visitors (Do Not Track respected).
          </span>
        </div>
        <button className="an-refresh" onClick={load}>{loading ? "…" : "Refresh"}</button>
      </div>

      <div className="an-tiles">
        {tiles.map((x, i) => (
          <div className="an-tile" key={i}>
            <div className="an-tile-label">{x.label}</div>
            <div className="an-tile-big">{x.big} <span className="an-tile-unit">visits</span></div>
            <div className="an-tile-sub">{x.uniq} unique visitor{x.uniq === 1 ? "" : "s"}</div>
          </div>
        ))}
      </div>

      <div className="an-grid">
        <div className="an-card an-span2">
          <h3>Visits per day <span className="an-muted">(last 14 days)</span></h3>
          <div className="an-spark">
            {s.daily.map((d, i) => (
              <div className="an-spark-col" key={i} title={`${d.date}: ${d.count} visit${d.count === 1 ? "" : "s"}`}>
                <div className="an-spark-track">
                  <div className="an-spark-bar" style={{ height: `${(d.count / dailyMax) * 100}%` }} />
                </div>
                <span className="an-spark-x">{d.date.slice(8)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="an-card">
          <h3>Devices</h3>
          <div className="an-seg">
            <span className="an-seg-fill mobile" style={{ width: `${(s.devices.mobile / devTotal) * 100}%` }} />
            <span className="an-seg-fill desktop" style={{ width: `${(s.devices.desktop / devTotal) * 100}%` }} />
            <span className="an-seg-fill tablet" style={{ width: `${(s.devices.tablet / devTotal) * 100}%` }} />
          </div>
          <div className="an-legend">
            <span><i className="dot mobile" /> Mobile {s.devices.mobile}</span>
            <span><i className="dot desktop" /> Desktop {s.devices.desktop}</span>
            <span><i className="dot tablet" /> Tablet {s.devices.tablet}</span>
          </div>
        </div>

        <div className="an-card">
          <h3>Top pages</h3>
          <Bars items={s.top_pages} />
        </div>
        <div className="an-card">
          <h3>Where they came from</h3>
          <Bars items={s.top_sources} color="var(--lead)" />
        </div>
      </div>

      {es && (
        <div className="an-card">
          <h3>Landing-page engagement{" "}
            <span className="an-muted">(/lp/, last 30 days &middot; {es.sessions_30d} session{es.sessions_30d === 1 ? "" : "s"})</span>
          </h3>
          {es.sessions_30d === 0 ? (
            <div className="an-empty">No landing-page behaviour recorded yet.</div>
          ) : (
            <>
              <div className="an-eng-tiles">
                <div><b>{fmtDur(es.avg_dwell_ms)}</b><span>avg time on page</span></div>
                <div><b>{es.avg_max_scroll_pct}%</b><span>avg scroll depth</span></div>
                <div><b>{es.pct_saw_form}%</b><span>saw the form</span></div>
                <div><b>{es.pct_focused_form}%</b><span>focused the form</span></div>
                <div><b>{es.pct_submitted}%</b><span>submitted</span></div>
                <div><b>{es.total_call_taps}</b><span>call taps</span></div>
                <div><b>{es.total_quote_taps}</b><span>quote taps</span></div>
              </div>
              {es.form_abandon_by_field && es.form_abandon_by_field.length > 0 && (
                <div className="an-eng-abandon">
                  <h4>Where visitors abandon the form</h4>
                  <Bars items={es.form_abandon_by_field.map((f) => ({ name: f.field, count: f.count }))} color="var(--lead)" />
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="an-card">
        <h3>Recent visitors <span className="an-muted">({recent.length}) &middot; click a row for the behaviour breakdown</span></h3>
        <div className="an-table-wrap">
          <table className="an-table">
            <thead>
              <tr>
                <th>When</th><th>Page</th><th>Source</th><th>Device</th>
                <th>Browser</th><th>OS</th><th>Lang</th><th>IP</th><th>Behaviour</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && (
                <tr><td colSpan={9} className="an-empty">No visits recorded yet.</td></tr>
              )}
              {recent.map((v, i) => {
                const e = v.engagement;
                const open = selected === i;
                return (
                  <React.Fragment key={i}>
                    <tr
                      className={"an-row-click" + (open ? " an-row-open" : "")}
                      onClick={() => setSelected(open ? null : i)}
                    >
                      <td className="an-nowrap">
                        <span className="an-caret">{open ? "▾" : "▸"}</span>
                        {fmtTime(v.ts)}{v.new ? <span className="an-new">new</span> : null}
                      </td>
                      <td className="an-mono">{v.path}</td>
                      <td>{v.source}</td>
                      <td>{v.device}</td>
                      <td>{v.browser}</td>
                      <td>{v.os}</td>
                      <td>{v.lang}</td>
                      <td className="an-mono an-ip">{v.ip}</td>
                      <td className="an-nowrap">
                        {e
                          ? <span className="an-beh">{fmtDur(e.dwell_ms)} &middot; {e.max_scroll}%{e.submitted ? " ✅" : (e.form_focus ? " ✏️" : "")}</span>
                          : <span className="an-beh-none">&mdash;</span>}
                      </td>
                    </tr>
                    {open && (
                      <tr className="an-detail-row">
                        <td colSpan={9}>
                          <EngagementDetail e={e} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Analytics;
